import React, { useId, useMemo } from "react";

import { SUMMARY_TRACK_COLOR_BASE, SUMMARY_TRACK_COLOR_LIGHT } from "../constants";
import { TRenderableRow } from "../types";
import { collectRenderableSegments } from "../utils/tree";
import { projectTimelineSegments, type TrackingTimeline } from "../tracking/player/timeline-status";
import { buildSummaryPaths, buildSummaryRuns } from "../utils/summary";
import { useSummaryMotion } from "./useSummaryMotion";

const PADDING = 4;
const BASELINE_HEIGHT = 3;

type CollectionHeaderRow = Extract<TRenderableRow, { kind: "collection-header" }>;

type CollectionSummaryProps = {
  collection: CollectionHeaderRow["data"];
  y: number;
  height: number;
  pixelsPerSecond: number;
  durationSeconds: number;
  trackingTimeline?: TrackingTimeline | null;
};

// Pan and viewport changes only move the parent group. Each collection keeps
// its target geometry until its data, zoom, duration, or row layout changes.
// SVG-only motion handles data updates without rerendering the player per frame.
const CollectionSummary = React.memo(
  ({ collection, y, height, pixelsPerSecond, durationSeconds, trackingTimeline = null }: CollectionSummaryProps) => {
    const gradientId = `summary-track-gradient-${useId()}`;
    const baseY = y + height - PADDING;
    const maxBarHeight = Math.max(1, height - PADDING * 2);
    const segments = useMemo(
      () => projectTimelineSegments(collectRenderableSegments(collection.children), trackingTimeline),
      [collection.children, trackingTimeline],
    );
    const density = useMemo(
      () => buildSummaryRuns(segments, pixelsPerSecond, durationSeconds),
      [segments, pixelsPerSecond, durationSeconds],
    );
    const paths = useMemo(
      () => buildSummaryPaths(density.runs, baseY, maxBarHeight, density.maxCount, density.totalWidth, BASELINE_HEIGHT),
      [density, baseY, maxBarHeight],
    );
    const { fillRef, strokeRef } = useSummaryMotion(
      paths,
      baseY,
      `${pixelsPerSecond}:${durationSeconds}:${y}:${height}`,
      trackingTimeline?.revision,
    );
    if (!paths) return null;
    return (
      <g>
        <defs>
          <linearGradient
            gradientUnits="userSpaceOnUse"
            id={gradientId}
            x1="0"
            x2="0"
            y1={baseY - maxBarHeight}
            y2={baseY}
          >
            <stop offset="0%" stopColor={SUMMARY_TRACK_COLOR_BASE} />
            <stop offset="45%" stopColor={SUMMARY_TRACK_COLOR_BASE} stopOpacity={0.72} />
            <stop offset="100%" stopColor={SUMMARY_TRACK_COLOR_LIGHT} />
          </linearGradient>
        </defs>
        <path d={paths.fill} fill={`url(#${gradientId})`} ref={fillRef} />
        <path
          d={paths.stroke}
          fill="none"
          ref={strokeRef}
          stroke={`url(#${gradientId})`}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeOpacity={0.5}
          strokeWidth={0.75}
        />
      </g>
    );
  },
);

CollectionSummary.displayName = "CollectionSummary";

type Props = {
  rows: TRenderableRow[];
  pixelsPerSecond: number;
  scrollLeft: number;
  scrollTop: number;
  durationSeconds: number;
  trackingTimeline?: TrackingTimeline | null;
  width: number;
  height: number;
};

export const SummaryOverlaySvg: React.FC<Props> = React.memo(
  ({ rows, pixelsPerSecond, scrollLeft, scrollTop, durationSeconds, width, height, trackingTimeline }) => {
    const collectionRows = useMemo(
      () => rows.filter((row): row is CollectionHeaderRow => row.kind === "collection-header" && row.depth === 0),
      [rows],
    );

    if (collectionRows.length === 0) return null;

    return (
      <svg aria-hidden height={height} style={{ position: "absolute", inset: 0, pointerEvents: "none" }} width={width}>
        <g transform={`translate(${-scrollLeft}, ${-scrollTop})`}>
          {collectionRows.map((row) => (
            <CollectionSummary
              collection={row.data}
              durationSeconds={durationSeconds}
              height={row.height}
              key={row.id}
              pixelsPerSecond={pixelsPerSecond}
              trackingTimeline={trackingTimeline}
              y={row.y}
            />
          ))}
        </g>
      </svg>
    );
  },
);

SummaryOverlaySvg.displayName = "SummaryOverlaySvg";
